import { BRAND, ALL_SOLUTION_SLUGS, SERVICES, getFxSolutionBySlug, getServiceBySlug, serviceSlug } from "./constants";

/** Canonical site origin — override at build time with REACT_APP_SITE_URL */
export const SITE_URL = (
  process.env.REACT_APP_SITE_URL || `https://${BRAND.domain}`
).replace(/\/$/, "");

const DEFAULT_OG_IMAGE = `${SITE_URL}/brand-mark.svg`;

export const DEFAULT_SEO = {
  title: `${BRAND.name} | Forex Broker CRM, Liquidity & Prop-Firm Technology`,
  description:
    `${BRAND.name} delivers operator-led forex technology: CRM, liquidity integration, copy trading, prop-firm tooling, and compliance-ready web and mobile delivery for global desks.`,
  keywords:
    "forex broker technology, forex CRM, prop firm CRM, liquidity bridge, MT4 MT5 hosting, forex web design, broker software, funded trader platform, KYC AML broker",
  image: DEFAULT_OG_IMAGE,
  type: "website",
};

const STATIC_PAGES = {
  "/": {
    title: `${BRAND.name} | Forex Broker Technology, CRM & Prop-Firm Solutions`,
    description:
      "Grow your forex or prop-firm operation with integrated CRM, liquidity, risk tooling, copy trading, and custom engineering — production timelines measured in weeks, not quarters.",
    keywords: DEFAULT_SEO.keywords,
  },
  "/about": {
    title: `About ${BRAND.name} | Broker Technology Built by Operators`,
    description:
      "Meet the markets specialists and senior engineers behind FX Broker Xpert's CRM, liquidity, risk, and digital systems for modern broker operations.",
    keywords: "forex technology company, broker software team, prop firm engineering",
  },
  "/services": {
    title: `Broker Services | Web, Mobile & CRM — ${BRAND.name}`,
    description:
      "Marketing sites, dynamic platforms, iOS and Android apps, plus CRM consulting, rollout, and integration — all delivered by the engineers behind our trading platform.",
    keywords: "forex web development, broker mobile apps, CRM implementation forex",
  },
  "/web-tech": {
    title: `Web-Tech Standards | React Engineering — ${BRAND.name}`,
    description:
      "Performance budgets, accessibility, SEO-ready React builds, and production observability — the engineering standards FX Broker Xpert applies to every web delivery.",
    keywords: "react broker website, forex web performance, broker frontend engineering",
  },
  "/contact": {
    title: `Contact ${BRAND.name} | Book a Broker Technology Discovery Call`,
    description:
      "Talk to FX Broker Xpert about CRM, liquidity, prop-firm, or custom broker software. Book a discovery call and hear back within one business day.",
    keywords: "contact forex technology vendor, broker software quote",
  },
  "/regulations": {
    title: `Regulations & Compliance | ${BRAND.name}`,
    description:
      "KYC, AML, and partner-vetting frameworks for broker and funded-program operators. Technology vendor only — no trading or client funds on our books.",
    keywords: "forex compliance technology, broker KYC AML software",
  },
};

function withDefaults(overrides) {
  return {
    ...DEFAULT_SEO,
    ...overrides,
    image: overrides.image || DEFAULT_OG_IMAGE,
    type: overrides.type || "website",
  };
}

/** Resolve SEO metadata for the current pathname (no query/hash). */
export function getSeoForPath(pathname) {
  const path = pathname.replace(/\/$/, "") || "/";

  if (STATIC_PAGES[path]) {
    return withDefaults(STATIC_PAGES[path]);
  }

  const solutionMatch = path.match(/^\/solutions\/([^/]+)$/);
  if (solutionMatch) {
    const solution = getFxSolutionBySlug(solutionMatch[1]);
    if (solution) {
      const desc = solution.paragraphs?.[0] || solution.desc;
      return withDefaults({
        title: `${solution.title} | ${solution.parentSection} — ${BRAND.name}`,
        description: desc.length > 160 ? `${desc.slice(0, 157)}…` : desc,
        keywords: `${solution.title.toLowerCase()}, forex ${solution.group.toLowerCase()}, ${BRAND.name}`,
        type: "article",
      });
    }
  }

  const serviceMatch = path.match(/^\/services\/([^/]+)$/);
  if (serviceMatch) {
    const service = getServiceBySlug(serviceMatch[1]);
    if (service) {
      const desc = service.paragraphs?.[0] || service.desc;
      return withDefaults({
        title: `${service.title} | Services — ${BRAND.name}`,
        description: desc.length > 160 ? `${desc.slice(0, 157)}…` : desc,
        keywords: `${service.title.toLowerCase()}, broker ${service.title.toLowerCase()}, ${BRAND.name}`,
        type: "article",
      });
    }
  }

  return withDefaults(STATIC_PAGES["/"]);
}

export function canonicalUrl(pathname, search = "") {
  const path = pathname.replace(/\/$/, "") || "/";
  return `${SITE_URL}${path}${search}`;
}

/** All indexable paths for sitemap generation. */
export function getAllSitemapPaths() {
  const staticPaths = Object.keys(STATIC_PAGES);
  const solutionPaths = ALL_SOLUTION_SLUGS.map((slug) => `/solutions/${slug}`);
  const servicePaths = SERVICES.map((s) => `/services/${serviceSlug(s.title)}`);
  return [...new Set([...staticPaths, ...solutionPaths, ...servicePaths])];
}

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: BRAND.name,
    url: SITE_URL,
    logo: `${SITE_URL}${BRAND.logo}`,
    email: BRAND.email,
    telephone: BRAND.phone,
    description: DEFAULT_SEO.description,
    sameAs: Object.values(BRAND.socials).filter((u) => u && u !== "#"),
  };
}

export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: BRAND.name,
    url: SITE_URL,
    description: DEFAULT_SEO.description,
    publisher: { "@type": "Organization", name: BRAND.name },
  };
}

/** Product/service page structured data. */
export function serviceJsonLd({ name, description, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url,
    provider: { "@type": "Organization", name: BRAND.name, url: SITE_URL },
    areaServed: "Worldwide",
  };
}
